<script setup lang="ts">
import {
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogOverlay,
  AlertDialogPortal,
  AlertDialogRoot,
  AlertDialogTitle,
} from "reka-ui"
import { Button, Icon } from "@/components/ui"
import { useModal, useTranslation } from "@/composables"
import { FreemiumUpgradePromptModalKey } from "@/symbols"

interface FreemiumUpgradePromptModalProps {
  eventName: string
  isLoading?: boolean
}

defineProps<FreemiumUpgradePromptModalProps>()

const emit = defineEmits<{
  (e: "confirm"): void
  (e: "cancel"): void
}>()

const { t } = useTranslation()
const { isActive, toggle } = useModal(FreemiumUpgradePromptModalKey)

function onOpenChange(open: boolean) {
  if (open || !isActive.value)
    return

  emit("cancel")
  toggle()
}

function handleConfirm() {
  emit("confirm")
}
</script>

<template>
  <AlertDialogRoot :open="isActive" @update:open="onOpenChange">
    <AlertDialogPortal>
      <Transition name="freemium-upgrade-prompt-modal">
        <AlertDialogOverlay
          v-if="isActive"
          class="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4"
          data-test="freemium-upgrade-prompt-modal"
        >
          <AlertDialogContent class="freemium-upgrade-prompt-panel w-[480px] max-w-[85vw] outline-none">
            <div class="relative flex flex-col overflow-hidden rounded-xl bg-white shadow-lg">
              <div class="flex flex-col gap-2 p-6 pb-4">
                <AlertDialogTitle class="font-heading text-lg font-semibold leading-snug text-primary md:text-xl">
                  {{ t("onboarding.upgrade_prompt.title") }}
                </AlertDialogTitle>

                <AlertDialogDescription class="font-body text-sm leading-relaxed text-current/80 md:text-base">
                  {{ t("onboarding.upgrade_prompt.message", { eventName }) }}
                </AlertDialogDescription>

                <div class="mt-2 flex flex-col gap-3">
                  <div class="flex flex-col gap-0.5" data-test="freemium-upgrade-prompt-upgrade-option">
                    <p class="font-body text-sm font-semibold text-primary md:text-base">
                      {{ t("onboarding.upgrade_prompt.upgrade_option_title") }}
                    </p>
                    <p class="font-body text-sm leading-relaxed text-current/70">
                      {{ t("onboarding.upgrade_prompt.upgrade_option_description") }}
                    </p>
                  </div>

                  <div class="flex flex-col gap-0.5" data-test="freemium-upgrade-prompt-create-new-option">
                    <p class="font-body text-sm font-semibold text-primary md:text-base">
                      {{ t("onboarding.upgrade_prompt.create_new_option_title") }}
                    </p>
                    <p class="font-body text-sm leading-relaxed text-current/70">
                      {{ t("onboarding.upgrade_prompt.create_new_option_description") }}
                    </p>
                  </div>
                </div>
              </div>

              <div class="border-t border-primary/10 px-6 py-4">
                <div class="flex flex-col-reverse gap-3 sm:flex-row sm:justify-end">
                  <AlertDialogCancel as-child>
                    <Button
                      variant="tertiary"
                      class="w-full sm:w-auto"
                      style="white-space: normal"
                      :disabled="isLoading"
                      data-test="freemium-upgrade-prompt-cancel"
                    >
                      {{ t("onboarding.upgrade_prompt.create_new_button") }}
                    </Button>
                  </AlertDialogCancel>

                  <Button
                    class="w-full sm:w-auto"
                    style="white-space: normal"
                    :disabled="isLoading"
                    data-test="freemium-upgrade-prompt-confirm"
                    @click="handleConfirm"
                  >
                    <template v-if="isLoading">
                      {{ t("onboarding.upgrade_prompt.upgrade_button") }} <Icon icon="Loading" data-test="loading-icon" />
                    </template>
                    <template v-else>
                      {{ t("onboarding.upgrade_prompt.upgrade_button") }}
                    </template>
                  </Button>
                </div>
              </div>
            </div>
          </AlertDialogContent>
        </AlertDialogOverlay>
      </Transition>
    </AlertDialogPortal>
  </AlertDialogRoot>
</template>

<style>
.freemium-upgrade-prompt-modal-enter-active,
.freemium-upgrade-prompt-modal-leave-active {
  transition: opacity 0.25s ease;
}

.freemium-upgrade-prompt-modal-enter-from,
.freemium-upgrade-prompt-modal-leave-to {
  opacity: 0;
}

.freemium-upgrade-prompt-modal-enter-active .freemium-upgrade-prompt-panel,
.freemium-upgrade-prompt-modal-leave-active .freemium-upgrade-prompt-panel {
  transition:
    transform 0.3s cubic-bezier(0.16, 1, 0.3, 1),
    opacity 0.25s ease;
  transform-origin: center center;
}

.freemium-upgrade-prompt-modal-enter-from .freemium-upgrade-prompt-panel,
.freemium-upgrade-prompt-modal-leave-to .freemium-upgrade-prompt-panel {
  opacity: 0;
  transform: scale(0.96);
}
</style>
